import { getApi } from '../api';
import { getDeviceAndUserStore, getAccountStore } from '../storage/stores';
import { captureException } from '@sentry/browser';
import { obtainFcmToken } from './obtainFcmToken';

/**
 * Ensure FCM token is registered and synced to backend
 *
 * Combines token acquisition and backend sync. Call on first session init.
 *
 * @throws Error when token acquisition or sync fails
 */
export const ensureFcmTokenRegistered = async (): Promise<void> => {
  await obtainFcmToken();
  await syncFcmTokenToBackend();
};

/**
 * Sync FCM token to backend
 *
 * Sends stored token to backend API. Idempotent: skips if token already sent.
 * Updates fcm.lastSentToken on success.
 *
 * @throws Error when stores are not initialized or backend call fails
 */
export const syncFcmTokenToBackend = async (): Promise<void> => {
  try {
    const deviceUserData = getDeviceAndUserStore().get();

    if (!deviceUserData?.fcm?.pushToken) {
      throw new Error('No FCM token found in storage');
    }

    const { pushToken: token, lastSentToken } = deviceUserData.fcm;

    if (lastSentToken === token) {
      return;
    }

    const accountData = getAccountStore().get();
    if (!accountData) {
      throw new Error('Account store not initialized');
    }

    await getApi().pushNotificationToken.post(
      {
        platform: 'FCM_Chrome',
        registration_id: token,
      } as const,
      {},
      {
        accountId: accountData.accountId,
        deviceUid: deviceUserData.device,
        userUid: deviceUserData.user,
      }
    );

    await getDeviceAndUserStore().set({
      fcm: {
        ...deviceUserData.fcm,
        lastSentToken: token,
      },
    });
  } catch (error) {
    console.error('Error syncing FCM token:', error);
    captureException(error as Error);
    throw error;
  }
};
